import { useContext, useEffect, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { SmartContext } from '../../../library/Core/SmartContext';
import { getCompanyImage, getStudentImage } from '../../../common/styles/Images';
import AdaptiveLogo from '../../../common/controls/AdaptiveLogo';
import InitialsAvatar from '../../../library/SimpleControls/InitialsAvatar';
import {
    canExportToExcel,
    exportToExcel,
    formatDesignationDisplay,
    formatFullName,
    getDomainValueForCode,
    isEmpty,
    isSchoolAdmin,
} from '../../../library/Core/SmartFunctions';
import SessionContext from '../../../library/Core/SessionContext';
import { jobPlace, pickDisplayJob } from '../../../library/Service/currentJobDisplay';
import EntrepreneurDirectoryService, {
    COMPANY_ANNUAL_REVENUE_DOMAIN,
    FAMILY_MANAGED_CODE,
    FIRST_GENERATION_CODE,
} from './EntrepreneurDirectoryService';

const FOUNDER_PILL: Record<string, { label: string; className: string }> = {
    [FIRST_GENERATION_CODE]: { label: '1st GEN', className: 'ent-dir-pill ent-dir-pill--first-gen' },
    [FAMILY_MANAGED_CODE]: { label: 'FAMILY', className: 'ent-dir-pill ent-dir-pill--family' },
};

const EntrepreneurCardGridControl = () => {
    const { state, dispatch } = useContext(SmartContext);
    const { sessionState } = useContext(SessionContext);
    const students: any[] = (state as any).data?.students || [];
    const pagination = (state as any).data?.pagination;
    const showRevenue = isSchoolAdmin();

    const [displayed, setDisplayed] = useState<any[]>(students);
    const [hasMore, setHasMore] = useState(pagination?.hasMore ?? false);
    const [loadingMore, setLoadingMore] = useState(false);

    const fetchMore = async () => {
        if (!hasMore || loadingMore) return;
        setLoadingMore(true);
        await EntrepreneurDirectoryService.fetchNextPage(state, dispatch);
        setLoadingMore(false);
    };

    useEffect(() => {
        setDisplayed(students);
        setHasMore(pagination?.hasMore ?? false);
    }, [students, pagination]);

    const profileLink = (uuid: string) => `/alumni/${uuid}/profile`;

    const lookup = (id: any, domain: string) => getDomainValueForCode(id, domain, state);
    const shownJob = (alumni: any) => pickDisplayJob(alumni?.currentJobsJson, 'ENTREPRENEURSHIP');

    const companyName = (alumni: any) => {
        const { job } = shownJob(alumni);
        return job ? jobPlace(job, lookup).name : '';
    };

    const companyLogo = (alumni: any) => {
        const { job } = shownJob(alumni);
        return getCompanyImage(job ? jobPlace(job, lookup).logo || '' : '');
    };

    const locationLine = (alumni: any) => {
        const city = getDomainValueForCode(alumni.residingCityCode, 'CITY_CODE', state);
        const country = getDomainValueForCode(alumni.residingCountryCode, 'COUNTRY_CODE', state);
        return [city, country].filter((v) => v && v !== alumni.residingCityCode && v !== alumni.residingCountryCode).join(', ')
            || [city, country].filter(Boolean).join(', ');
    };

    const metaLine = (alumni: any) => {
        const { job } = shownJob(alumni);
        const designation = formatDesignationDisplay(job?.designation, state, {
            workingStatusCode: job?.jobCategory,
        });
        const industryCode = job?.industryCode;
        const industry = industryCode ? getDomainValueForCode(industryCode, 'INDUSTRY_CODE', state) : '';
        const year = job?.establishmentYear;
        const est = year ? `est. ${year}` : '';
        return [designation, industry && industry !== industryCode ? industry : industry, est].filter(Boolean).join(' · ');
    };

    const revenueLabel = (alumni: any) => {
        const { job } = shownJob(alumni);
        const raw = job?.annualRevenue;
        if (typeof raw !== 'string' || isEmpty(raw)) return 'Revenue not disclosed';
        return `Revenue ${getDomainValueForCode(raw, COMPANY_ANNUAL_REVENUE_DOMAIN, state)}`;
    };

    const noRecordsFound = () => (
        <div className="row g-2 g-md-4 alumni-showcase" id="FilterData">
            <div className="no_listed_items_blk">
                <div className="mt-0">
                    <div className="max-470 mx-auto">
                        <img
                            className="mt-2 mb-4 float_horizontal_animate"
                            src={`${process.env.REACT_APP_IMAGE_BASEPATH}/alumni_directory_nodata.svg`}
                            alt="No data"
                        />
                        <p className="font-18 font-400 mb-4 text-center">
                            No entrepreneurs found matching your search criteria.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );

    const showExportButton = canExportToExcel(sessionState);
    const handleExportToExcel = () => exportToExcel(students, 'entrepreneur-directory', state);

    return (
        <section className="alumni_directory_details bg-white container-fluid">
            <div className="max-1140 pt-4 pb-5 mx-auto">
                <div className="d-flex justify-content-between align-items-center mb-3">
                    {showExportButton && (
                        <button
                            className="btn btn-success d-flex align-items-center"
                            disabled={state?.flags?.isDataLoading || !(state?.data?.students?.length > 0)}
                            onClick={handleExportToExcel}>
                            Export to Excel
                        </button>
                    )}
                    <div className="alumni_count" id="UsersCounts">
                        {(state as any)?.data?.pagination?.total ?? (state as any)?.data?.totalCount ?? students.length} Entrepreneurs Found
                    </div>
                </div>

                <InfiniteScroll dataLength={displayed.length} next={fetchMore} hasMore={hasMore} loader="">
                    <div className="overflow-hidden">
                        <div className="row g-2 g-md-4 alumni-showcase">
                            {displayed.map((student: any) => {
                                const name = student.name || formatFullName(student.firstName, student.lastName);
                                const { job: currentJob, count: currentRoleCount } = shownJob(student);
                                const founderPill = FOUNDER_PILL[String(currentJob?.founderStatus || '')];
                                return (
                                    <div key={`${student.uuid}-${student.instituteId || ""}`} className="col-lg-4 col-md-6 col-sm-6 Dusers">
                                        <div className="card h-100 border rounded-3 ent-dir-card">
                                            <div className="card-body ent-dir-card__head">
                                                <div className="d-flex align-items-start position-relative">
                                                    <a
                                                        className="ent-dir-card__photo flex-shrink-0"
                                                        target="_blank"
                                                        href={profileLink(student.uuid)}
                                                        rel="noreferrer">
                                                        {student.profilePictureFilePath ? (
                                                            <img src={getStudentImage(student)} alt={name} />
                                                        ) : (
                                                            <InitialsAvatar
                                                                firstName={student.firstName}
                                                                lastName={student.lastName}
                                                                name={name}
                                                            />
                                                        )}
                                                    </a>
                                                    <div className="flex-1 min-w-0 ps-3 pe-5">
                                                        <h3 className="ent-dir-card__name mb-1">
                                                            <a href={profileLink(student.uuid)} target="_blank" rel="noreferrer">
                                                                {name}
                                                            </a>
                                                        </h3>
                                                        <div className="ent-dir-card__program">
                                                            {[student.programName, student.passOutYear].filter(Boolean).join(', ')}
                                                        </div>
                                                        {locationLine(student) && (
                                                            <div className="ent-dir-card__location">
                                                                <span aria-hidden="true">📍</span> {locationLine(student)}
                                                            </div>
                                                        )}
                                                    </div>
                                                    {founderPill && (
                                                        <span className={founderPill.className}>{founderPill.label}</span>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="ent-dir-card__company">
                                                <AdaptiveLogo
                                                    className="ent-dir-card__logo"
                                                    src={companyLogo(student)}
                                                    alt="Company Logo"
                                                    fallbackShape="square"
                                                />
                                                <div className="min-w-0">
                                                    <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
                                                        <div className="ent-dir-card__company-name">
                                                            {companyName(student)}
                                                        </div>
                                                        {currentRoleCount > 1 && (
                                                            <span
                                                                className="badge rounded-pill fw-medium px-2 py-1 bg-success bg-opacity-25 border border-success border-opacity-10 text-success-emphasis"
                                                                role="button"
                                                                title="Review current roles">
                                                                {currentRoleCount} current roles
                                                            </span>
                                                        )}
                                                    </div>
                                                    {metaLine(student) && (
                                                        <div className="ent-dir-card__meta">{metaLine(student)}</div>
                                                    )}
                                                </div>
                                            </div>

                                            {showRevenue && (
                                                <div className="ent-dir-card__revenue">
                                                    <span
                                                        className={`ent-dir-revenue-badge${
                                                            isEmpty(currentJob?.annualRevenue) ? ' ent-dir-revenue-badge--empty' : ''
                                                        }`}>
                                                        {revenueLabel(student)}
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </InfiniteScroll>

                {!(state as any).flags?.isDataLoading && students.length === 0 && noRecordsFound()}
            </div>
        </section>
    );
};

export default EntrepreneurCardGridControl;
